import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { CloseIcon } from "stream-chat-react";
import { toast } from "sonner";
import moment from "moment-timezone";
import { requestInterpretation } from "../../../api/EcgReports";
import { withoutEmojis } from "../../../utils/emoji";
import { getItems } from "../../../api/settings";
import {
  ANALYTICS_EVENTS,
  logAnalyticsEvent,
  resolveInterpreterType,
} from "../../../analytics";
import { Link } from "react-router-dom";
import useSendForInterpretation from "../../../hooks/useSendForInterpretation";
import TopUpCreditsModal from "../../Credits/TopUpCreditsModal";

const SendInterpretationDialog = ({ open, onClose, report, onSent }) => {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [notes, setNotes] = useState("");
  const [buttonLoading, setButtonLoading] = useState(false);
  const [paySendOpen, setPaySendOpen] = useState(false);

  const {
    credits: creditBalance,
    isUnlimitedCredits,
    outOfCredits,
    canPayAndSend,
    pricePerCredit,
    handlePayAndSend,
    paying: payAndSendLoading,
  } = useSendForInterpretation();

  const hasSelection = !!selected?.value;

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const res = await getItems();

        if (res?.success && Array.isArray(res.data)) {
          const formattedItems = res.data.map((item, index) => ({
            value: item.id || `item-${index}`,
            label: item.name.trim(),
            userType: item.userType || "unknown",
          }));

          setItems(formattedItems);

          // Automatically select if there is only one recipient
          if (formattedItems.length === 1) {
            setSelected({
              value: formattedItems[0].value,
              userType: formattedItems[0].userType,
            });
          }
        }
      } catch (err) {
        console.error("Error fetching items:", err);
      }
    };

    fetchItems();
  }, []);

  const handleSendForInterpretation = async (notesOverride) => {
    const reportId = report?.id;
    // selected is the {value, userType} object the Select stores, so the
    // interpreter bucket is read straight off it — no lookup back into items.
    const interpreterType = resolveInterpreterType(selected?.userType);
    const effectiveNotes = notesOverride !== undefined ? notesOverride : notes;

    try {
      setButtonLoading(true);

      const res = await requestInterpretation({
        ids: [reportId],
        notes: effectiveNotes,
        sendTo: selected,
        collectionId: report?.collection_id,
      });

      if (res.success) {
        logAnalyticsEvent(ANALYTICS_EVENTS.INTERPRETATION_REQUESTED, {
          report_id: reportId,
          interpreter_type: interpreterType,
        });
        toast.success(res.message);
        window.dispatchEvent(new Event("credits:changed"));
        onClose();
        onSent();
      } else {
        logAnalyticsEvent(ANALYTICS_EVENTS.INTERPRETATION_REQUEST_FAILED, {
          report_id: reportId,
          interpreter_type: interpreterType,
          failure_reason: res.message,
        });
        toast.error(res.message);
        // Rethrow (already logged/toasted above) so a pay-and-send caller
        // knows the send itself failed even though payment already went
        // through - the catch block below only handles thrown exceptions.
        const alreadyHandled = new Error(res.message || "Failed to send report");
        alreadyHandled.alreadyHandled = true;
        throw alreadyHandled;
      }
    } catch (e) {
      if (!e?.alreadyHandled) {
        logAnalyticsEvent(ANALYTICS_EVENTS.INTERPRETATION_REQUEST_FAILED, {
          report_id: reportId,
          interpreter_type: interpreterType,
          failure_reason: "request_threw",
        });
        console.error("Error sending report:", e);
        toast.error("Failed to send report");
      }
      throw e;
    } finally {
      setButtonLoading(false);
    }
  };

  const handlePaySendConfirm = async (quantity, paySendNotes) => {
    await handlePayAndSend(quantity, () => handleSendForInterpretation(paySendNotes));
    setPaySendOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "475px",
          maxWidth: "calc(100% - 32px)",
          borderRadius: "14px",
          padding: "6px 20px 14px",
          margin: "16px",
          boxShadow: "0 12px 40px rgba(0, 0, 0, 0.18)",
          overflow: "visible",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 0 12px",
        }}
      >
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: 600,
            color: "#22223b",
          }}
        >
          Send for Interpretation
        </Typography>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "#777",
            padding: "4px",
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          padding: "0 !important",
          overflow: "visible",
        }}
      >
        {/* Patient Information */}
        <Box
          sx={{
            backgroundColor: "#f8f7fc",
            border: "1px solid #e2e0ed",
            borderRadius: "11px",
            padding: "14px",
            marginBottom: "16px",
          }}
        >
          <Typography
            sx={{
              fontSize: "15px",
              fontWeight: 600,
              color: "#24243b",
              marginBottom: "4px",
            }}
          >
            #{report?.collection_id} |{" "}
            <span style={{ fontWeight: 600 }}>
              {report?.user_data?.first_name}{" "}
              {report?.user_data?.last_name}
            </span>
          </Typography>

          <Typography
            sx={{
              fontSize: "13px",
              color: "#69677d",
              marginBottom: "3px",
            }}
          >
            {report?.user_data?.userAge} years |{" "}
            {report?.user_data?.gender}
          </Typography>

          <Typography
            sx={{
              fontSize: "12px",
              color: "#77758a",
            }}
          >
            {moment(report?.createdAt).format("D MMM YYYY, h:mm a")}
          </Typography>
        </Box>

        {/* Send To */}
        <Box sx={{ marginBottom: "14px" }}>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              color: "#66647a",
              marginBottom: "6px",
            }}
          >
            Send to
          </Typography>

          <Select
            fullWidth
            displayEmpty
            size="small"
            value={JSON.stringify(selected) || ""}
            onChange={(e) => setSelected(JSON.parse(e.target.value))}
            renderValue={(value) => {
              if (!value) {
                return (
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#999",
                    }}
                  >
                    Select recipient
                  </Typography>
                );
              }

              const parsed = JSON.parse(value);

              const selectedItem = items.find(
                (item) => item.value === parsed.value,
              );

              return (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#29283d",
                    }}
                  >
                    {selectedItem?.label || "Select recipient"}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#999",
                    }}
                  >
                    🔒 Only recipient on your plan
                  </Typography>
                </Box>
              );
            }}
            sx={{
              height: "44px",
              borderRadius: "9px",
              backgroundColor: "#fff",

              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#deddea",
              },

              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#c9c5dd",
              },

              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#6c45ff",
              },

              "& .MuiSelect-select": {
                padding: "10px 13px",
              },
            }}
          >
            {items.map((item) => (
              <MenuItem
                key={item.value}
                value={JSON.stringify({
                  value: item.value,
                  userType: item.userType,
                })}
              >
                {item.label}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Notes */}
        <Box sx={{ marginBottom: "14px" }}>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              color: "#66647a",
              marginBottom: "6px",
            }}
          >
            Notes
          </Typography>

          <TextField
            multiline
            rows={2}
            fullWidth
            placeholder="Anything the doctor should know"
            value={notes}
            onChange={withoutEmojis((e) => setNotes(e.target.value))}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "9px",
                fontSize: "14px",
                padding: "10px 12px",
                backgroundColor: "#fff",
              },

              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#deddea",
              },

              "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
              {
                borderColor: "#c9c5dd",
              },

              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                borderColor: "#6c45ff",
              },

              "& textarea::placeholder": {
                color: "#888",
                opacity: 1,
              },
            }}
          />
        </Box>

        {/* Credit Information */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            backgroundColor: outOfCredits ? "#fdecec" : "#eeeaff",
            border: outOfCredits ? "1px solid #f3c8c8" : "1px solid #ddd4ff",
            borderRadius: "9px",
            padding: "10px 12px",
            marginBottom: "14px",
          }}
        >
          <Box
            sx={{
              width: "18px",
              height: "18px",
              borderRadius: "50%",
              backgroundColor: outOfCredits ? "#d71920" : "#7651ff",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "11px",
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            ₹
          </Box>

          <Typography
            sx={{
              fontSize: "12px",
              color: outOfCredits ? "#c62828" : "#6348cc",
            }}
          >
            {outOfCredits ? (
              canPayAndSend ? (
                "You're out of interpretation credits. Pay to top up and send this report in one step."
              ) : (
                "Credits are pooled for the whole organisation, not per user. Ask your account owner to top up."
              )
            ) : isUnlimitedCredits ? (
              "Unlimited interpretation credits available on your plan."
            ) : (
              <>
                1 credit will be used · Balance{" "}
                <strong>
                  {creditBalance ?? "—"}
                  {creditBalance !== null ? ` → ${creditBalance - 1}` : ""}
                </strong>
              </>
            )}
          </Typography>
        </Box>

        {/* Auto Send Info */}
        <Typography
          sx={{
            fontSize: "12px",
            color: "#716e83",
            marginBottom: "14px",
          }}
        >
          You can auto-send all reports to Spandan Expert from{" "}
          <Link
            to="/settings/credits"
            style={{
              color: "#6744ff",
              fontWeight: 600,
              textDecoration: "none",
            }}
          >
            Settings
          </Link>
        </Typography>

        {/* Send Button */}
        {hasSelection && (
          <Button
            fullWidth
            variant="contained"
            disabled={buttonLoading || (outOfCredits && !canPayAndSend)}
            onClick={() =>
              outOfCredits && canPayAndSend
                ? setPaySendOpen(true)
                : handleSendForInterpretation().catch(() => {})
            }
            sx={{
              height: "44px",
              borderRadius: "9px",
              backgroundColor: "#6c45ff",
              color: "#fff",
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 600,
              boxShadow: "none",

              "&:hover": {
                backgroundColor: "#5d38eb",
                boxShadow: "none",
              },

              "&:disabled": {
                backgroundColor: "#b5a5ff",
                color: "#fff",
              },
            }}
          >
            {buttonLoading
              ? "Sending Report..."
              : outOfCredits
              ? canPayAndSend
                ? "Pay & Send"
                : "Insufficient credits"
              : "Send Report"}
          </Button>
        )}
      </DialogContent>

      <TopUpCreditsModal
        open={paySendOpen}
        onClose={() => setPaySendOpen(false)}
        mode="paySend"
        currentBalance={creditBalance}
        pricePerCredit={pricePerCredit}
        report={report}
        recipientLabel={
          items.find((item) => item.value === selected?.value)?.label ||
          "Spandan MD Doctor"
        }
        loading={payAndSendLoading || buttonLoading}
        onPayAndSend={handlePaySendConfirm}
      />
    </Dialog>
  );
};

export default SendInterpretationDialog;